import SearchIcon from '@mui/icons-material/Search';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Stack,
  CircularProgress,
  IconButton,
  Box,
} from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import debounce from 'lodash.debounce';
import React, { useState, useEffect, useCallback } from 'react';

const cellSx = {
  fontSize: '19px',
};

const maxWidthSx = {
  maxWidth: 125,
  wordBreak: 'break-word',
};

export default function WOVehicleListModal({
  open,
  onClose,
  vehicles,
  rooftops,
  onSearch,
  onSelectVehicle,
  loading,
}) {
  const [search, setSearch] = useState('');
  const [selectedRooftop, setSelectedRooftop] = useState('All');
  const [selectedId, setSelectedId] = useState(null);

  const handleSelect = vehicle => {
    setSelectedId(vehicle?.vin);
    onSelectVehicle(vehicle);
    onClose();
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSearch = useCallback(
    debounce((value, rooftop) => {
      onSearch(value, rooftop);
    }, 1000),
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => {
    if (open && rooftops.length > 0) {
      const firstRooftopId = rooftops[0].id;
      setSelectedRooftop(firstRooftopId);
      setSearch('');
      setSelectedId(null);
      onSearch('', firstRooftopId);
    }

    if (!open) {
      setSearch('');
      setSelectedRooftop('');
      setSelectedId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, rooftops]);

  useEffect(() => {
    if (open && selectedRooftop) {
      onSearch(search, selectedRooftop);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRooftop]);

  const handleChange = e => {
    setSearch(e.target.value);
    handleSearch(e.target.value, selectedRooftop);
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick') {
          onClose(event, reason);
        }
      }}
      maxWidth="lg"
      fullWidth
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ padding: '20px 40px 20px 25px' }}
      >
        <DialogTitle
          component="h2"
          sx={{ p: 0, m: 0, fontSize: '1.25rem', fontWeight: 600 }}
        >
          List of Vehicles
        </DialogTitle>

        <Stack direction="row" gap={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Select Rooftop</InputLabel>
            <Select
              data-testid="rooftop-select"
              value={selectedRooftop}
              label="Select Rooftop"
              onChange={e => setSelectedRooftop(e.target.value)}
            >
              {rooftops?.map((item, i) => (
                <MenuItem key={i} value={item?.id}>
                  {item?.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            size="small"
            placeholder="Search"
            value={search}
            onChange={handleChange}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    edge="end"
                    onClick={() => onSearch(search, selectedRooftop)}
                    sx={{ cursor: 'pointer' }}
                    aria-label="trigger search"
                  >
                    <SearchIcon sx={{ color: 'action.active' }} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Stack>
      </Stack>

      <DialogContent
        sx={{
          pt: 0,
          pb: '20px',
          px: '24px',
          overflow: 'hidden',
          maxHeight: '600px',
        }}
      >
        <TableContainer
          component={Paper}
          sx={{
            maxHeight: 600,
            overflowY: 'auto',
            border: '1px solid #D0D0D0',
          }}
        >
          <Table stickyHeader size="small" aria-label="vehicle table">
            <TableHead>
              <TableRow>
                {[
                  'VIN#',
                  'Stock#',
                  'Year',
                  'Make',
                  'Model',
                  'Trim',
                  'Type',
                  'Ext - Color',
                  '',
                ].map((header, index, array) => (
                  <TableCell
                    key={index}
                    sx={{
                      position: 'sticky',
                      top: 0,
                      backgroundColor: 'background.paper',
                      padding: '15px',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      '&::after':
                        index !== array.length - 1
                          ? {
                              content: '""',
                              position: 'absolute',
                              top: '15px',
                              bottom: '15px',
                              right: 0,
                              width: '2px',
                              backgroundColor: '#C8C8C8',
                            }
                          : {},
                    }}
                  >
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            {loading ? (
              <TableBody>
                <TableRow>
                  <TableCell
                    colSpan={9}
                    sx={{ height: '600px', position: 'relative', border: 0 }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'rgba(255, 255, 255, 0.6)',
                        zIndex: 1,
                      }}
                    />
                    <Stack
                      justifyContent="center"
                      alignItems="center"
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        zIndex: 2,
                      }}
                    >
                      <CircularProgress size={50} sx={{ color: 'grey.700' }} />
                    </Stack>
                  </TableCell>
                </TableRow>
              </TableBody>
            ) : (
              <TableBody>
                {vehicles?.length > 0 ? (
                  vehicles?.map(v => (
                    <TableRow key={v.vin} hover selected={selectedId === v.vin}>
                      <TableCell sx={{ ...cellSx, padding: '18px' }}>
                        {v.vin}
                      </TableCell>
                      <TableCell sx={[cellSx, maxWidthSx]}>
                        {v.stockNumber}
                      </TableCell>
                      <TableCell sx={[cellSx, maxWidthSx]}>{v.year}</TableCell>
                      <TableCell sx={[cellSx, maxWidthSx]}>{v.make}</TableCell>
                      <TableCell sx={[cellSx, maxWidthSx]}>{v.model}</TableCell>
                      <TableCell sx={[cellSx, maxWidthSx]}>{v.trim}</TableCell>
                      <TableCell sx={[cellSx, maxWidthSx]}>{v.type}</TableCell>
                      <TableCell sx={[cellSx, maxWidthSx]}>
                        {v.colorCode}
                      </TableCell>
                      <TableCell sx={[cellSx, maxWidthSx]} align="center">
                        <Button
                          variant={
                            selectedId === v.vin ? 'contained' : 'outlined'
                          }
                          size="medium"
                          onClick={() => handleSelect(v)}
                          aria-label={`Select vehicle ${v.vin}`}
                        >
                          {selectedId === v.vin ? 'Selected' : 'Select'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={9}
                      sx={{
                        textAlign: 'center',
                        py: 2,
                        px: '24px',
                        fontSize: '18px',
                      }}
                    >
                      No vehicles found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            )}
          </Table>
        </TableContainer>
      </DialogContent>

      <Stack
        direction="row"
        justifyContent="flex-end"
        sx={{ padding: '0 24px 20px' }}
      >
        <Button variant="outlined" color="error" onClick={onClose}>
          Cancel
        </Button>
      </Stack>
    </Dialog>
  );
}
